<template>
	<div
		class="bg-primary-25 w-full p-6 print:break-inside-avoid print:pt-3 print:pb-1"
	>
		<h2
			class="form-title pb-6 text-2xl font-bold print:mb-3 print:border-b-2 print:border-slate-900 print:pt-2 print:pb-3 print:text-sm/0"
		>
			{{ t('plan.sectionTitle.personsResponsible') }}
		</h2>

		<CopyButtonList
			field-or-section="peopleResponsible"
			:active-index="activePlanIndex"
			class="print:hidden"
		/>

		<section>
			<FormKit
				id="peopleResponsible"
				name="peopleResponsible"
				type="repeater"
				:draggable="false"
				add-button
				:insert-control="true"
				:up-control="false"
				:down-control="false"
			>
				<template #default="{ value }">
					<div
						class="section-grid-row grid grid-cols-3 gap-2 *:w-full *:max-w-none"
					>
						<FormKit
							type="textarea"
							auto-height
							name="firstName"
							:label="t('person.firstName')"
						/>
						<FormKit
							type="textarea"
							auto-height
							name="lastName"
							:label="t('person.lastName')"
						/>
						<FormKit
							type="textarea"
							auto-height
							name="relationship"
							:label="t('plan.relationship')"
						/>
					</div>
					<div
						class="section-grid-row grid grid-cols-3 gap-2 *:w-full *:max-w-none"
					>
						<FormKit
							type="textarea"
							auto-height
							name="city"
							:label="t('person.city')"
						/>
						<FormKit
							type="autocomplete"
							:label="t('person.state')"
							name="state"
							:placeholder="
								t('plan.placeholders.select', {
									placeholderLabel: t('person.state'),
								})
							"
							:options="stateOptions"
							clear-search-on-open
						/>
						<FormKit
							type="textarea"
							auto-height
							name="zip"
							:label="t('person.zip')"
							validation="matches:/^\d{5}(-\d{4})?$/"
							validation-visibility="live"
						/>
					</div>
					<div
						class="section-grid-row grid grid-cols-2 *:w-full *:max-w-none *:px-1 *:pb-1"
					>
						<FormKit
							type="tel"
							name="phone"
							:label="t('person.phone')"
							placeholder="xxx-xxx-xxxx"
							:validation="[
								['matches', /^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/],
							]"
							:validation-messages="{
								matches: `${t('plan.validationMessages.phone')}`,
							}"
							validation-visibility="live"
						/>
						<FormKit
							type="email"
							name="email"
							:label="t('person.email')"
							validation="email"
							validation-visibility="dirty"
							:placeholder="t('plan.placeholders.email')"
						/>
					</div>
					<div
						class="section-grid-row grid grid-cols-1 *:w-full *:max-w-none *:px-1"
						:class="{ 'print:hidden': !value?.notes?.trim() }"
					>
						<FormKit
							type="textarea"
							auto-height
							name="notes"
							:label="t('plan.notes')"
							:outer-class="{ 'mb-0': true }"
							:placeholder="t('plan.placeholders.personsResponsibleNotes')"
							validation="length:0,10000"
							validation-visibility="live"
						/>
					</div>
				</template>
			</FormKit>

			<div class="group flex print:hidden">
				<PlusCircleIcon
					class="text-accent-400 group-hover:text-accent-500 mr-1 size-6"
					aria-hidden="true"
				/>
				<button
					class="push text-accent-400 group-hover:text-accent-500"
					type="button"
					@click="addPersonResponsible"
				>
					{{ t('plan.addPersonResponsible') }}
				</button>
			</div>
		</section>
	</div>
</template>

<script setup lang="ts">
import CopyButtonList from '@/components/presentation/CopyButtonList.vue'
import { stateOptions } from '@/data/stateOptions'
import { usePresentationLocale } from '@/i18n'
import { FormKit } from '@formkit/vue'
import PlusCircleIcon from '@heroicons/vue/24/solid/PlusCircleIcon'

const { t } = usePresentationLocale()

defineProps<{
	activePlanIndex: number
}>()

const emit = defineEmits<{
	(e: 'add-person'): void
}>()

function addPersonResponsible() {
	emit('add-person')
}
</script>
